import { useEffect, useMemo, useSyncExternalStore } from 'react'

import {
  holdFrame,
  lastRetryPhoto,
  makeSelectSearchVm,
  onSentShot,
  platformResources,
  platformSearchHealth,
  platformSearchPhoto,
  platformSearchWarmup,
  releaseFrame,
  rememberRetryPhoto,
  rememberSentShot,
  searchHealthRequested,
  searchRun,
  sentShotUrl,
  type SearchActions,
  type SearchVm,
  type SearchVmInput,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { useMyAccess } from './useMyAccess'

/** Сколько позиций просить у поиска; как у сервера по умолчанию. */
const SEARCH_LIMIT = 20

/** Подготовка службы текста не чаще раза в две минуты на вкладку (сервер ограничивает частоту сам; лишние запросы не нужны). */
const WARMUP_MIN_INTERVAL_MS = 120_000
let lastWarmupAt = 0

/** Идущий поиск (один на вкладку): его прерывает «Отменить» с любого экрана. */
let inflight: { abort: () => void } | null = null

/**
 * Поиск по фото — один на экран поиска и окно сканера (RP-FB4). Файл в store не попадает: в запрос уходит `frameId`,
 * кадр снимается, когда запрос закончился. Результат — в кэше мутации с постоянным ключом, поэтому выдача, снятая в
 * сканере, остаётся на экране поиска, а возврат во вкладку показывает прежнюю. Отправленный снимок тоже общий:
 * его адрес — `sentShot`, вверху выдачи (RP-FB26).
 */
export function usePhotoSearch(): {
  vm: SearchVm
  actions: Omit<SearchActions, 'openScanner'>
  reset: () => void
  sentShot: string | null
} {
  const dispatch = useAppDispatch()
  platformSearchHealth.useQuerySubscription()
  platformResources.useQuerySubscription()
  const [search, result] = platformSearchPhoto.useMutation({ fixedCacheKey: 'platform-search-photo' })
  const [warmUp] = platformSearchWarmup.useMutation()
  const access = useMyAccess()

  // Экран поиска и сканер открылись — службе текста пора проснуться, пока человек наводит камеру (RP-FB52). Ошибку не показываем.
  useEffect(() => {
    const now = Date.now()
    if (now - lastWarmupAt < WARMUP_MIN_INTERVAL_MS) return
    lastWarmupAt = now
    void warmUp().unwrap().catch(() => undefined)
  }, [warmUp])

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectSearchVm()
    const selectHealth = platformSearchHealth.select()
    const selectResources = platformResources.select()
    return (state: RootState, last: typeof result, rights: typeof access, preliminary: SearchVmInput['preliminary']) =>
      selectPackageVm({
        health: selectHealth(state),
        search: last,
        resources: selectResources(state),
        access: rights,
        preliminary,
      })
  }, [])
  // Предварительная выдача идущего поиска приходит событием потока, а не ответом мутации: берём её из хода поиска.
  const preliminary = useSyncExternalStore(searchRun.subscribe, () => searchRun.get().preliminary)
  const vm = useAppSelector((state) => selectVm(state, result, access, preliminary))
  const { reset } = result

  const actions = useMemo(() => {
    const start = (photo: File) => {
      rememberSentShot(photo)
      const frameId = holdFrame(photo)
      const request = search({ frameId, limit: SEARCH_LIMIT })
      inflight = request
      void request
        .unwrap()
        .catch(() => undefined)
        .finally(() => {
          releaseFrame(frameId)
          if (inflight === request) inflight = null
        })
    }
    return {
      search: (photo: File) => {
        rememberRetryPhoto(photo)
        start(photo)
      },
      cancel: () => inflight?.abort(),
      // Тот же кадр ещё раз: человеку не нужно снимать или выбирать заново.
      retry: () => {
        const photo = lastRetryPhoto()
        if (photo !== null) start(photo)
      },
      checkHealth: () => dispatch(searchHealthRequested()),
    }
  }, [dispatch, search])

  const sentShot = useSyncExternalStore(onSentShot, sentShotUrl)
  return { vm, actions, reset, sentShot }
}
